import type { Question } from '../engine/types';

/**
 * The 14 Board AI Literacy questions split into a standalone module so both
 * the schema and the recommendation library can import it without forming a
 * circular dependency.
 */
export const BOARD_AI_LITERACY_QUESTIONS: Question[] = [
  // Axis: Governance Maturity (8 questions)
  {
    id: 'q1',
    axis: 'governance',
    band: 'strategy',
    allowSkip: true,
    prompt:
      'Does your board have a written AI strategy or charter that states where AI fits and where it does not in your business?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes, board-approved and reviewed in the last 12 months', weight: 4 },
      { id: 'b', label: 'Yes, but more than 12 months old', weight: 2 },
      { id: 'c', label: 'We have something informal but not approved', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },
  {
    id: 'q2',
    axis: 'governance',
    band: 'strategy',
    allowSkip: true,
    prompt: 'Has the board named an executive lead with explicit accountability for AI strategy and risk?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes, formally named in board papers with KPIs', weight: 4 },
      { id: 'b', label: 'Yes, informally — someone is the de facto AI lead', weight: 2 },
      { id: 'c', label: 'It has been discussed but not decided', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },
  {
    id: 'q3',
    axis: 'governance',
    band: 'governance',
    allowSkip: true,
    prompt: 'Do you have a written AI policy or acceptable-use policy in force across the business?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes, board-approved and reviewed in the last 12 months', weight: 4 },
      { id: 'b', label: 'Yes, but more than 12 months old or not formally approved', weight: 2 },
      { id: 'c', label: 'Drafted but not yet in force', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },
  {
    id: 'q4',
    axis: 'governance',
    band: 'governance',
    allowSkip: true,
    prompt:
      'Do you have visibility of which AI tools your staff are actually using right now (sanctioned and unsanctioned)?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — we have conducted a shadow-AI inventory in the last 6 months', weight: 4 },
      { id: 'b', label: 'We know our sanctioned tools but not what staff are using unofficially', weight: 2 },
      { id: 'c', label: 'We rely on staff to self-declare AI use', weight: 1 },
      { id: 'd', label: 'We do not have visibility', weight: 0 },
    ],
  },
  {
    id: 'q5',
    axis: 'governance',
    band: 'governance',
    allowSkip: true,
    prompt: 'Do you maintain an AI risk register with named owners and review cadence?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes, reviewed at least quarterly by the board or board committee', weight: 4 },
      { id: 'b', label: 'Yes, but reviewed irregularly', weight: 2 },
      { id: 'c', label: 'AI risks sit in the general enterprise risk register', weight: 1 },
      { id: 'd', label: 'No dedicated AI risk register', weight: 0 },
    ],
  },
  {
    id: 'q6',
    axis: 'governance',
    band: 'risk',
    allowSkip: true,
    prompt:
      'Have your top three AI vendors been formally assessed for Privacy Act compliance, data residency, and contractual data-use terms?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — all three assessed, with documented sign-off', weight: 4 },
      { id: 'b', label: 'One or two have been formally assessed', weight: 2 },
      { id: 'c', label: 'We trust the vendor’s marketing claims — no formal assessment', weight: 1 },
      { id: 'd', label: 'We are not sure who our top three AI vendors are', weight: 0 },
    ],
  },
  {
    id: 'q7',
    axis: 'governance',
    band: 'risk',
    allowSkip: true,
    prompt:
      'If a Privacy Act breach occurred tomorrow via an AI tool, do you know who would handle it, in what window, and how the board would be notified?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — our incident response plan covers AI scenarios explicitly', weight: 4 },
      { id: 'b', label: 'General incident response plan exists, but AI scenarios are not called out', weight: 2 },
      { id: 'c', label: 'We would work it out at the time', weight: 1 },
      { id: 'd', label: 'No incident response plan', weight: 0 },
    ],
  },
  {
    id: 'q8',
    axis: 'governance',
    band: 'risk',
    allowSkip: true,
    prompt:
      'Is the board actively tracking sector-specific AI guidance as it evolves (APRA, ASIC, AUSTRAC, OAIC, Voluntary AI Safety Standard, ISO/IEC 42001)?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes, with a designated regulatory horizon scan reported to the board', weight: 4 },
      { id: 'b', label: 'Informally — when something prominent is reported in the media', weight: 2 },
      { id: 'c', label: 'We rely on our compliance team or external advisers to flag it', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },

  // Axis: Director Capability (6 questions)
  {
    id: 'q9',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt: 'Has the board completed structured AI literacy training in the last 12 months?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — structured training with an outcome assessment', weight: 4 },
      { id: 'b', label: 'Yes — an external presenter ran a session, but no follow-up', weight: 2 },
      { id: 'c', label: 'Informal reading and discussion only', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },
  {
    id: 'q10',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt:
      'Could a typical director on your board confidently explain the difference between generative AI, predictive AI, and agentic AI in plain language?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — this was covered in our literacy work', weight: 4 },
      { id: 'b', label: 'Most directors could give a workable explanation', weight: 2 },
      { id: 'c', label: 'One or two directors could; most could not', weight: 1 },
      { id: 'd', label: 'No', weight: 0 },
    ],
  },
  {
    id: 'q11',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt:
      'Do directors feel confident challenging the executive on AI decisions without deferring to the technical team?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — this happens routinely in AI discussions', weight: 4 },
      { id: 'b', label: 'Some directors do, others defer', weight: 2 },
      { id: 'c', label: 'Rarely — there is a perceived technical knowledge gap', weight: 1 },
      { id: 'd', label: 'No — the executive view is generally accepted', weight: 0 },
    ],
  },
  {
    id: 'q12',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt:
      'When did your board last review a structured AI report (inventory, value capture, incidents) — not just hear a verbal update?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'In the last quarter', weight: 4 },
      { id: 'b', label: 'In the last 6 months', weight: 2 },
      { id: 'c', label: 'In the last 12 months', weight: 1 },
      { id: 'd', label: 'Never — or more than 12 months ago', weight: 0 },
    ],
  },
  {
    id: 'q13',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt:
      'Does at least one director on your board have direct senior-level technology experience (CIO, CTO, technology executive, or technology board roles)?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — at least one director with dedicated tech executive background', weight: 4 },
      { id: 'b', label: 'One director with adjacent technology experience', weight: 2 },
      { id: 'c', label: 'Not currently, but we are actively recruiting for it', weight: 1 },
      { id: 'd', label: 'No — and it is not on the skills matrix', weight: 0 },
    ],
  },
  {
    id: 'q14',
    axis: 'capability',
    band: 'capability',
    allowSkip: true,
    prompt:
      'Could your board confidently approve or reject a proposed AI use case based on its risk profile, not just its commercial upside?',
    type: 'single-choice',
    options: [
      { id: 'a', label: 'Yes — with structured criteria the board has agreed', weight: 4 },
      { id: 'b', label: 'Usually, based on collective judgement', weight: 2 },
      { id: 'c', label: 'We tend to defer to the executive recommendation', weight: 1 },
      { id: 'd', label: 'No — we approve based on the business case as presented', weight: 0 },
    ],
  },
];
